
import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Settings, AlertCircle, CheckCircle, Info } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAutomationSettings, AutomationSettings } from '@/hooks/useAutomationSettings';

export const MasterAutomationControl = () => {
  const { settings, isLoading, updateSettings, isPending, error } = useAutomationSettings();
  const [localSettings, setLocalSettings] = useState<AutomationSettings>({
    frequency: 'daily',
    notifyByEmail: false,
    autoClean: true,
    enabled: false
  });

  // Update local settings when data is loaded
  React.useEffect(() => {
    if (settings) {
      setLocalSettings(settings);
    }
  }, [settings]);

  const handleSave = () => {
    console.log('Saving automation settings:', localSettings);
    updateSettings(localSettings);
  };

  const handleToggleEnabled = (enabled: boolean) => {
    const newSettings = { ...localSettings, enabled };
    setLocalSettings(newSettings);
    updateSettings(newSettings);
  };

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          <span>Loading automation settings...</span>
        </div>
      </Card>
    );
  }

  // Show info message instead of error for missing settings
  const showInfoMessage = !settings && !error;
  const showErrorMessage = error && !settings;

  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 mb-4">
        <Settings className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold">Master Automation Control</h3>
      </div>

      <div className="space-y-4">
        {/* Show info message for first-time setup */}
        {showInfoMessage && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              No automation settings found. Configure your automation preferences below and save to get started.
            </AlertDescription>
          </Alert>
        )}

        {/* Show error only if there's a real error and no settings */}
        {showErrorMessage && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Unable to load automation settings. Using default values. Error: {typeof error === 'string' ? error : 'Unknown error'}
            </AlertDescription>
          </Alert>
        )}

        {/* Master Enable/Disable */}
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div>
            <Label className="text-base font-medium">Enable Automation</Label>
            <p className="text-sm text-muted-foreground">
              Master switch for all automated processes
            </p>
          </div>
          <Switch
            checked={localSettings.enabled}
            onCheckedChange={handleToggleEnabled}
            disabled={isPending}
          />
        </div>

        {/* Frequency Setting */}
        <div className="space-y-2">
          <Label>Automation Frequency</Label>
          <Select 
            value={localSettings.frequency} 
            onValueChange={(value) => setLocalSettings({ ...localSettings, frequency: value })}
            disabled={!localSettings.enabled || isPending}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select frequency" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="hourly">Hourly</SelectItem>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Email Notifications */}
        <div className="flex items-center justify-between">
          <div>
            <Label>Email Notifications</Label>
            <p className="text-sm text-muted-foreground">
              Receive email alerts for automation events
            </p>
          </div>
          <Switch
            checked={localSettings.notifyByEmail}
            onCheckedChange={(checked) => setLocalSettings({ ...localSettings, notifyByEmail: checked })}
            disabled={!localSettings.enabled || isPending}
          />
        </div>

        {/* Auto Clean Data */}
        <div className="flex items-center justify-between">
          <div>
            <Label>Auto Clean Data</Label>
            <p className="text-sm text-muted-foreground">
              Automatically clean and process incoming data
            </p>
          </div>
          <Switch
            checked={localSettings.autoClean}
            onCheckedChange={(checked) => setLocalSettings({ ...localSettings, autoClean: checked })}
            disabled={!localSettings.enabled || isPending}
          />
        </div>

        {/* Status Display */}
        {localSettings.enabled && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span className="text-sm text-green-800">
                Automation is active - Running {localSettings.frequency}
                {localSettings.lastRun && ` (Last run: ${new Date(localSettings.lastRun).toLocaleString()})`}
              </span>
            </div>
          </div>
        )}

        {/* Save Button */}
        <Button 
          onClick={handleSave}
          disabled={isPending}
          className="w-full"
        >
          {isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving Settings...
            </>
          ) : (
            <>
              <Settings className="mr-2 h-4 w-4" />
              Save Automation Settings
            </>
          )}
        </Button>
      </div>
    </Card>
  );
};
